/**
 * theme.js — the single source of truth for how the CRM looks.
 *
 * Every color, size, radius and motion value used by the new UI lives here.
 * Screens never hard-code colors; they use the ui/ components (which read these
 * values through Ant Design's theme) or, for the rare custom CSS, the CSS
 * variables that ui.css defines from the same values.
 *
 * Design direction (see client/UI_GUIDE.md):
 *   calm & clean admin UI — light gray page, white cards with thin borders,
 *   one indigo accent used only for primary actions / links / selection,
 *   data colors (statuses, tags) shown as soft tinted badges.
 *
 * Changing a value here restyles the whole app. That is the point.
 */

// ---- Raw design tokens ------------------------------------------------------
export const tokens = {
  color: {
    accent: '#5B5BD6', // indigo: primary buttons, links, selected rows/menu
    accentHover: '#4C4CC4',
    accentSoft: '#EEEEFC', // background of selected / active items

    pageBg: '#F7F7F8', // app background behind cards
    surface: '#FFFFFF', // cards, tables, drawers
    border: '#EAEAEC', // thin borders between surfaces
    borderStrong: '#D9D9DE',

    text: '#18181B', // primary text
    textSecondary: '#71717A', // meta text, captions, table secondary lines
    textTertiary: '#A1A1AA', // placeholders, disabled

    success: '#16A34A',
    warning: '#D97706',
    danger: '#DC2626',
    info: '#2563EB',

    whatsapp: '#25D366',
  },

  font: {
    family: "'Heebo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
    sizeBody: 14, // body + tables
    sizeSmall: 12, // captions, secondary lines
    sizeSection: 16, // section / card titles
    sizePage: 22, // page titles
  },

  radius: {
    control: 6, // buttons, inputs
    card: 10, // cards, drawers, modals
    pill: 999, // badges, chips
  },

  // 4px grid
  space: (n) => n * 4,

  // Motion — follows Emil Kowalski's rules (see UI_GUIDE.md "Motion"):
  //  * UI animation stays under 300ms
  //  * strong custom ease-out for enter/exit, never ease-in
  //  * nothing that happens dozens of times a day animates (row hover, sort, typing)
  motion: {
    easeOut: 'cubic-bezier(0.23, 1, 0.32, 1)',
    easeInOut: 'cubic-bezier(0.77, 0, 0.175, 1)',
    press: '160ms', // button press feedback
    fast: '150ms', // dropdowns, tooltips
    base: '240ms', // drawers, modals
  },

  layout: {
    siderWidth: 232,
    siderCollapsedWidth: 72,
    headerHeight: 56,
    contentMaxWidth: 1440,
  },
};

// ---- Ant Design theme (passed to <ConfigProvider theme={antTheme}>) ----------
// Docs: https://ant.design/docs/react/customize-theme
export const antTheme = {
  token: {
    colorPrimary: tokens.color.accent,
    colorLink: tokens.color.accent,
    colorSuccess: tokens.color.success,
    colorWarning: tokens.color.warning,
    colorError: tokens.color.danger,
    colorInfo: tokens.color.info,

    colorText: tokens.color.text,
    colorTextSecondary: tokens.color.textSecondary,
    colorTextTertiary: tokens.color.textTertiary,
    colorBorder: tokens.color.borderStrong,
    colorBorderSecondary: tokens.color.border,
    colorBgLayout: tokens.color.pageBg,
    colorBgContainer: tokens.color.surface,

    fontFamily: tokens.font.family,
    fontSize: tokens.font.sizeBody,
    borderRadius: tokens.radius.control,
    borderRadiusLG: tokens.radius.card,
    controlHeight: 36,
    wireframe: false,

    // Ant's own motion values, aligned with ours
    motionDurationFast: '0.12s',
    motionDurationMid: '0.18s',
    motionDurationSlow: '0.24s',
    motionEaseOut: tokens.motion.easeOut,
    motionEaseInOut: tokens.motion.easeInOut,

    colorBgMask: 'rgba(24, 24, 27, 0.32)', // backdrop behind drawers/modals

    boxShadowSecondary: '0 8px 24px rgba(24, 24, 27, 0.08), 0 2px 6px rgba(24, 24, 27, 0.04)',
  },
  components: {
    Layout: {
      bodyBg: tokens.color.pageBg,
      headerBg: tokens.color.surface,
      siderBg: tokens.color.surface,
      headerHeight: tokens.layout.headerHeight,
      headerPadding: `0 ${tokens.space(6)}px`,
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: tokens.color.accentSoft,
      itemSelectedColor: tokens.color.accent,
      itemHoverBg: '#F4F4F5',
      itemBorderRadius: 8,
      itemMarginInline: 10,
      itemHeight: 40,
      iconSize: 17,
      activeBarBorderWidth: 0,
    },
    Table: {
      headerBg: '#FAFAFB',
      headerColor: tokens.color.textSecondary,
      headerSplitColor: 'transparent',
      rowHoverBg: '#FAFAFB',
      rowSelectedBg: '#F6F6FE',
      rowSelectedHoverBg: '#F0F0FD',
      borderColor: tokens.color.border,
      cellPaddingBlock: 12,
      cellPaddingInline: 14,
    },
    Card: {
      headerFontSize: tokens.font.sizeSection,
    },
    Drawer: {
      footerPaddingBlock: 12,
      footerPaddingInline: 24,
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontWeight: 500,
    },
    Tag: {
      defaultBg: '#F4F4F5',
    },
  },
};

export default tokens;
